import { toSignal } from '@angular/core/rxjs-interop';

import {
  Component,
  computed,
  Inject,
  LOCALE_ID,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ProfileDataService } from '../../services/data/profile-data.service';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { SharedModule } from '../../shared/shared.module';
import { MatDialog } from '@angular/material/dialog';
import { EditProfileSettingsDialogComponent } from '../dialogs/edit-profile-settings-dialog/edit-profile-settings-dialog.component';
import { EmployeeSettings } from '../../model/employee/employee-settings';
import {
  DEFAULT_PREFERRED_LANGUAGE,
  getPreferredLanguageOption,
} from '../../model/employee/employee-settings-language';
import { CountryDataService } from '../../shared/phone-number-input/services/country-data.service';
import { Language } from '../../util/language';
import { EditProfilePreferencesDialogComponent } from '../dialogs/edit-profile-preferences-dialog/edit-profile-preferences-dialog.component';
import { NotificationSettingsCardComponent } from '../notification-settings-card/notification-settings-card.component';

@Component({
  selector: 'app-profile-settings',
  imports: [
    SharedModule,
    MatCardModule,
    MatListModule,
    MatIconModule,
    NotificationSettingsCardComponent,
  ],
  templateUrl: './profile-settings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./profile-settings.component.scss'],
})
export class ProfileSettingsComponent {
  protected readonly settings$ = toSignal(
    this.profileDataService.employeeSettings$,
    { initialValue: null },
  );
  protected readonly defaultDisplayLanguage = 'de';
  protected readonly defaultPreferredLanguage = DEFAULT_PREFERRED_LANGUAGE;
  protected readonly defaultPhotoCountdownSeconds = 2;
  protected readonly defaultLiveImageDurationSeconds = 5;
  protected readonly defaultFavoriteSmsCountryCodes = ['AT', 'DE', 'CH'];
  protected readonly photoCountdown$ = computed(
    () =>
      this.settings$()?.photoCountdownSeconds ??
      this.defaultPhotoCountdownSeconds,
  );
  protected readonly liveImageDuration$ = computed(
    () =>
      this.settings$()?.liveImageDurationSeconds ??
      this.defaultLiveImageDurationSeconds,
  );
  protected readonly displayLanguageLabel$ = computed(
    () =>
      `${Language.getFlagFromLanguageCode(
        this.settings$()?.displayLanguage ?? this.defaultDisplayLanguage,
      )} ${Language.getDisplayNameFromLanguageCode(
        this.settings$()?.displayLanguage ?? this.defaultDisplayLanguage,
        this.locale,
      )}`,
  );
  protected readonly preferredLanguageLabel$ = computed(() => {
    const preferredLanguage =
      this.settings$()?.preferredLanguage ?? this.defaultPreferredLanguage;
    const option = getPreferredLanguageOption(preferredLanguage);

    return option ? `${option.flag} ${option.name}` : preferredLanguage;
  });

  constructor(
    private readonly profileDataService: ProfileDataService,
    private readonly dialog: MatDialog,
    private readonly countryDataService: CountryDataService,
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {}

  protected onEditProfileSettings(): void {
    const settings = this.settings$();
    if (!settings) {
      return;
    }

    this.dialog.open(EditProfilePreferencesDialogComponent, {
      maxWidth: '95vw',
      data: settings as EmployeeSettings,
    });
  }

  protected onEditCallSettings(): void {
    const settings = this.settings$();
    if (!settings) {
      return;
    }

    this.dialog.open(EditProfileSettingsDialogComponent, {
      maxWidth: '95vw',
      data: settings as EmployeeSettings,
    });
  }

  protected getFavoriteSmsCountriesText(
    settings: EmployeeSettings | null,
  ): string {
    const favoriteCountryCodes =
      settings?.favoriteSmsCountryCodes ?? this.defaultFavoriteSmsCountryCodes;

    const favoriteCountryNames = favoriteCountryCodes.map(
      (countryCode) =>
        this.countryDataService.getPhoneDataFromCountryCode(countryCode)
          ?.countryName ?? countryCode,
    );

    if (favoriteCountryNames.length === 0) {
      return $localize`Keine Favoriten`;
    }

    return favoriteCountryNames.join(', ');
  }
}
